import React, { useMemo } from "react";
import { Skeleton, Tooltip } from "@vibe/core";
import { ExternalPage, Delete } from "@vibe/icons";
import { TableDataInterface } from "./table.data.interface";
import { generateItemUrl } from "../../utils/mondayLinks";
import logger from "../../lib/logger";
import { useSlug } from "../../hooks/context/useSlug";
import { useLinkedBoardLocations } from "../../hooks/api";
import { resolveLinkedBoard, ResolvedBoard } from "./resolveLinkedBoard";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import { cn } from "../../utils/tailwind-merge";

/** Set to true to preview layout with 10 "Also on" chips */
const SIMULATE_10_BOARDS = false;

interface BoardsHeaderProps {
  isLoading: boolean;
  tableData: TableDataInterface;
  currentBoard: number;
  userId: number;
  /** Drops the bottom border/padding (the column dialog has nothing beneath the chips). */
  compact?: boolean;
  /**
   * Called once the remove modal opened for a chip has been closed (the SDK's
   * `openAppFeatureModal` promise resolves on close). The item view refreshes
   * through its socket; the column dialog uses this to refetch the chips.
   */
  onRemoveModalClosed?: () => void;
}

const BoardGroupChip = ({
  handleOpenItem,
  handleRemoveItem,
  currentBoard,
  boardId,
  boardName,
  groupName,
}: {
  handleOpenItem: () => void;
  handleRemoveItem: () => void;
  currentBoard: boolean;
  boardId: string;
  boardName?: string;
  groupName?: string;
}) => {
  const resolvedBoardName = boardName ?? "Private Board";
  const resolvedGroupName = groupName ?? "Group";

  return (
    <div
      onClick={handleOpenItem}
      className={cn(
        "group flex items-center gap-1 pt-1 pr-2 pb-1 pl-2 border border-solid rounded text-sm cursor-pointer",
        currentBoard
          ? "bg-primary-selected border-primary-color "
          : "bg-[#ffffff] border-[#c3c6d4] ",
      )}
    >
      <Tooltip zIndex={1210} content={`${resolvedBoardName} · ${resolvedGroupName}`}>
        <div
          className={cn(
            "flex items-center justify-center gap-[0.375rem] transition-colors px-1 rounded-[0.250rem]",
            currentBoard ? "hover:bg-[rgba(0,134,192,0.15)]" : "hover:bg-[rgba(103,104,121,0.1)]",
          )}
        >
          <span
            className={cn(
              "font-medium max-w-[200px] truncate",
              currentBoard ? "text-primary-color" : "text-[#323338]",
            )}
          >
            {resolvedBoardName}
          </span>
          <span
            className={cn(
              "flex-shrink-0",
              currentBoard ? "text-primary-color/60" : "text-light-secondary-text-color",
            )}
          >
            ·
          </span>
          <span
            className={cn(
              "max-w-[120px] truncate",
              currentBoard ? "text-primary-color/80" : "text-light-secondary-text-color",
            )}
          >
            {resolvedGroupName}
          </span>
          <ExternalPage
            className="flex-shrink-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            color={"#0073ea"}
            size={14}
          />
        </div>
      </Tooltip>

      <Delete
        onClick={(e) => {
          e.stopPropagation();
          handleRemoveItem();
        }}
        className="flex-shrink-0"
        color={"#676879"}
        size={14}
      />
    </div>
  );
};

const BoardsHeader = ({
  isLoading,
  tableData,
  currentBoard,
  userId,
  compact = false,
  onRemoveModalClosed,
}: BoardsHeaderProps) => {
  const { slug } = useSlug();
  const { locationByItemId } = useLinkedBoardLocations(tableData?.linkedBoards);

  const resolvedBoards = useMemo<ResolvedBoard[]>(
    () =>
      (tableData?.linkedBoards ?? []).map((board) =>
        resolveLinkedBoard(board, locationByItemId[board.itemId]),
      ),
    [tableData?.linkedBoards, locationByItemId],
  );

  // Bucketed by resolved boardId, so an item moved into the board being viewed
  // is shown as the current board rather than lingering under "Also on".
  const currentBoardData = resolvedBoards.find(
    (board) => board.boardId === currentBoard.toString(),
  );
  const handleOpenItem = (targetBoardId: string, targetItemId: string) => {
    try {
      const url = generateItemUrl(slug, targetBoardId, targetItemId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error: any) {
      logger.error("Error opening item in new tab", error);
    }
  };

  const handleRemoveItem = async (
    userId: string,
    targetItemId: string,
    targetBoardId: string,
    targetBoardName: string,
  ): Promise<void> => {
    try {
      await MondayApiQueryInstance.openAppFeatureModal(
        "/remove-modal",
        {
          userId: userId as string,
          targetItemId: targetItemId as string,
          targetBoardId: targetBoardId as string,
          boardName: targetBoardName as string,
        },
        "250px",
        "500px",
      );
      onRemoveModalClosed?.();
    } catch (error: any) {
      logger.error("Error opening remove item modal", error);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-3 items-start",
        !compact && "pb-4 border-t-0 border-b border-gray-200 border-solid border-x-0",
      )}
    >
      {/* Current Board */}
      <div>
        <div className="flex gap-[5px] justify-center items-center">
          {isLoading ? (
            <>
              <Skeleton type="text" size="p" height={24} />
            </>
          ) : (
            <>
              <p className="!font-figtree text-sm text-light-secondary-text-color">
                Current Board:
              </p>

              <BoardGroupChip
                handleOpenItem={() => {
                  handleOpenItem(currentBoard.toString(), currentBoardData?.itemId ?? "");
                }}
                currentBoard
                boardId={currentBoard.toString()}
                boardName={currentBoardData?.boardName ?? "Unknown Board"}
                groupName={currentBoardData?.groupName}
                handleRemoveItem={() =>
                  handleRemoveItem(
                    userId.toString(),
                    currentBoardData?.itemId!,
                    currentBoard.toString(),
                    currentBoardData?.boardName ?? "Unknown Board",
                  )
                }
              />
            </>
          )}
        </div>
      </div>
      {/* Other Boards */}
      <div className="flex flex-col gap-[5px] items-start">
        {isLoading ? (
          <>
            <Skeleton type="text" size="p" height={24} />
          </>
        ) : (
          <>
            <p className="!font-figtree text-sm text-light-secondary-text-color shrink-0">
              Also on:
            </p>

            <div className="flex flex-wrap gap-x-[10px] gap-y-[5px] items-center w-full min-w-0">
              {(() => {
                const otherBoards = resolvedBoards.filter(
                  (board) => board.boardId !== currentBoard.toString(),
                );
                const boardsToShow: ResolvedBoard[] = SIMULATE_10_BOARDS
                  ? Array.from({ length: 10 }, (_, i) =>
                      otherBoards[i]
                        ? { ...otherBoards[i], _sim: false }
                        : {
                            boardId: `sim-${i}`,
                            boardName: `Board ${i + 1}`,
                            itemId: `sim-${i}`,
                            _sim: true,
                          },
                    )
                  : otherBoards;
                return boardsToShow.map((board) => (
                  <BoardGroupChip
                    key={board.boardId}
                    boardName={board.boardName}
                    currentBoard={currentBoard.toString() === board.boardId}
                    boardId={board.boardId}
                    groupName={board._sim ? undefined : board.groupName}
                    handleOpenItem={() => {
                      if (!board._sim) handleOpenItem(board.boardId, board.itemId);
                    }}
                    handleRemoveItem={() =>
                      handleRemoveItem(
                        userId.toString(),
                        board.itemId,
                        board.boardId,
                        board.boardName ?? "Private Board",
                      )
                    }
                  />
                ));
              })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BoardsHeader;
